import React, { useEffect, useState, useRef, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon, SearchIcon, ChevronLeftIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
interface DisputesFiltersProps {
  disputeDateBasis: 'dispute-date' | 'payment-date';
  onDisputeDateBasisChange: (value: 'dispute-date' | 'payment-date') => void;
  paymentCountBasis: 'payment-count' | 'payment-volume';
  onPaymentCountBasisChange: (value: 'payment-count' | 'payment-volume') => void;
  includeEFW: 'include' | 'exclude';
  onIncludeEFWChange: (value: 'include' | 'exclude') => void;
  cardTypes: string[];
  onCardTypesChange: (types: string[]) => void;
  cardCountries: string[];
  onCardCountriesChange: (countries: string[]) => void;
  cardBrands: string[];
  onCardBrandsChange: (brands: string[]) => void;
  additionalFilters: string[];
  onAdditionalFiltersChange: (filters: string[]) => void;
}
type MoreFiltersView = 'menu' | 'input-method' | 'currency' | 'payment-country' | '3ds-outcome';
export function DisputesFilters({
  disputeDateBasis,
  onDisputeDateBasisChange,
  paymentCountBasis,
  onPaymentCountBasisChange,
  includeEFW,
  onIncludeEFWChange,
  cardTypes,
  onCardTypesChange,
  cardCountries,
  onCardCountriesChange,
  cardBrands,
  onCardBrandsChange,
  additionalFilters,
  onAdditionalFiltersChange
}: DisputesFiltersProps) {
  const {
    t
  } = useTranslation('analytics');
  const [showDisputeDateDropdown, setShowDisputeDateDropdown] = useState(false);
  const [showPaymentCountDropdown, setShowPaymentCountDropdown] = useState(false);
  const [showEFWDropdown, setShowEFWDropdown] = useState(false);
  const [showCardTypeDropdown, setShowCardTypeDropdown] = useState(false);
  const [showCardCountryDropdown, setShowCardCountryDropdown] = useState(false);
  const [showCardBrandDropdown, setShowCardBrandDropdown] = useState(false);
  const [showMoreFiltersDropdown, setShowMoreFiltersDropdown] = useState(false);
  const [moreFiltersView, setMoreFiltersView] = useState<MoreFiltersView>('menu');
  // Shared payment filters (internal state)
  const [paymentMethodCategories, setPaymentMethodCategories] = useState<string[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [fundingSourceTypes, setFundingSourceTypes] = useState<string[]>([]);
  const [showPMCategoryDropdown, setShowPMCategoryDropdown] = useState(false);
  const [showPMMethodDropdown, setShowPMMethodDropdown] = useState(false);
  const [showFundingSourceDropdown, setShowFundingSourceDropdown] = useState(false);
  // Additional filter states
  const [inputMethods, setInputMethods] = useState<string[]>([]);
  const [currencies, setCurrencies] = useState<string[]>([]);
  const [paymentCountries, setPaymentCountries] = useState<string[]>([]);
  const [threeDSOutcomes, setThreeDSOutcomes] = useState<string[]>([]);
  const disputeDateRef = useRef<HTMLDivElement>(null);
  const paymentCountRef = useRef<HTMLDivElement>(null);
  const efwRef = useRef<HTMLDivElement>(null);
  const cardTypeRef = useRef<HTMLDivElement>(null);
  const cardCountryRef = useRef<HTMLDivElement>(null);
  const cardBrandRef = useRef<HTMLDivElement>(null);
  const moreFiltersRef = useRef<HTMLDivElement>(null);
  // Shared payment filter refs
  const pmCategoryRef = useRef<HTMLDivElement>(null);
  const pmMethodRef = useRef<HTMLDivElement>(null);
  const fundingSourceRef = useRef<HTMLDivElement>(null);
  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (disputeDateRef.current && !disputeDateRef.current.contains(event.target as Node)) {
        setShowDisputeDateDropdown(false);
      }
      if (paymentCountRef.current && !paymentCountRef.current.contains(event.target as Node)) {
        setShowPaymentCountDropdown(false);
      }
      if (efwRef.current && !efwRef.current.contains(event.target as Node)) {
        setShowEFWDropdown(false);
      }
      if (cardTypeRef.current && !cardTypeRef.current.contains(event.target as Node)) {
        setShowCardTypeDropdown(false);
      }
      if (cardCountryRef.current && !cardCountryRef.current.contains(event.target as Node)) {
        setShowCardCountryDropdown(false);
      }
      if (cardBrandRef.current && !cardBrandRef.current.contains(event.target as Node)) {
        setShowCardBrandDropdown(false);
      }
      if (moreFiltersRef.current && !moreFiltersRef.current.contains(event.target as Node)) {
        setShowMoreFiltersDropdown(false);
        setMoreFiltersView('menu');
      }
      if (pmCategoryRef.current && !pmCategoryRef.current.contains(event.target as Node)) {
        setShowPMCategoryDropdown(false);
      }
      if (pmMethodRef.current && !pmMethodRef.current.contains(event.target as Node)) {
        setShowPMMethodDropdown(false);
      }
      if (fundingSourceRef.current && !fundingSourceRef.current.contains(event.target as Node)) {
        setShowFundingSourceDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const availableAdditionalFilters = [{
    id: 'input-method',
    label: 'Input method'
  }, {
    id: 'currency',
    label: 'Currency'
  }, {
    id: 'payment-country',
    label: 'Payment country'
  }, {
    id: '3ds-outcome',
    label: '3DS outcome'
  }];
  // Card countries with Malaysia first, then alphabetically
  const cardCountryOptions = ['Malaysia', 'Afghanistan', 'Åland Islands', 'Albania', 'Algeria', 'American Samoa', 'Andorra', 'Angola', 'Anguilla', 'Australia', 'Singapore', 'United Kingdom', 'United States'];
  // Payment countries with Malaysia first, then alphabetically
  const paymentCountryOptions = ['Malaysia', 'Afghanistan', 'Åland Islands', 'Albania', 'Algeria', 'American Samoa', 'Andorra', 'Angola', 'Anguilla', 'Australia', 'Singapore', 'United Kingdom', 'United States'];
  // Currency options with MYR first, then alphabetically
  const currencyOptions = ['MYR - Malaysian Ringgit', 'MYR - Malaysian Ringgit', 'AED - United Arab Emirates Dirham', 'AFN - Afghan Afghani', 'ALL - Albanian Lek', 'AMD - Armenian Dram', 'ANG - Netherlands Antillean Guilder', 'AOA - Angolan Kwanza', 'ARS - Argentine Peso', 'AUD - Australian Dollar', 'SGD - Singapore Dollar', 'USD - US Dollar'];
  const handleMoreFilterClick = (filterId: string) => {
    setMoreFiltersView(filterId as MoreFiltersView);
  };
  const handleBackToMenu = () => {
    setMoreFiltersView('menu');
  };
  const handleApplyFilter = (applyFn: () => void) => {
    applyFn();
    setShowMoreFiltersDropdown(false);
    setMoreFiltersView('menu');
  };
  return <>
      {/* Show rates based on row */}
      <div className="w-full flex flex-wrap items-center gap-3" data-id="element-1637">
        <span className="text-sm text-gray-600" data-id="element-1638">
          {t('components.DisputesFilters.show_rates_based_on')}
        </span>

        {/* Dispute date dropdown */}
        <div className="relative" ref={disputeDateRef} data-id="element-1639">
          <button onClick={() => setShowDisputeDateDropdown(!showDisputeDateDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-1640">
            <span data-id="element-1641">
              {disputeDateBasis === 'dispute-date' ? 'Dispute date' : 'Payment date'}
            </span>
            <ChevronDownIcon size={16} data-id="element-1642" />
          </button>

          {showDisputeDateDropdown && <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50" data-id="element-1643">
              <button onClick={() => {
            onDisputeDateBasisChange('dispute-date');
            setShowDisputeDateDropdown(false);
          }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${disputeDateBasis === 'dispute-date' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-1644">
                <div className="flex-1 min-w-0" data-id="element-1645">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1646">
                    {t('components.DisputesFilters.dispute_date')}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-1647">
                    {t('components.DisputesFilters.calculate_rates_based_on_the_date')}
                  </div>
                </div>
                {disputeDateBasis === 'dispute-date' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1648" />}
              </button>

              <button onClick={() => {
            onDisputeDateBasisChange('payment-date');
            setShowDisputeDateDropdown(false);
          }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${disputeDateBasis === 'payment-date' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-1649">
                <div className="flex-1 min-w-0" data-id="element-1650">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1651">
                    {t('components.DisputesFilters.payment_date')}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-1652">
                    {t('components.DisputesFilters.calculate_rates_based_on_the_date_2')}
                  </div>
                </div>
                {disputeDateBasis === 'payment-date' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1653" />}
              </button>
            </div>}
        </div>

        {/* Payment count dropdown */}
        <div className="relative" ref={paymentCountRef} data-id="element-1654">
          <button onClick={() => setShowPaymentCountDropdown(!showPaymentCountDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-1655">
            <span data-id="element-1656">
              {paymentCountBasis === 'payment-count' ? 'Payment count' : 'Payment volume'}
            </span>
            <ChevronDownIcon size={16} data-id="element-1657" />
          </button>

          {showPaymentCountDropdown && <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50" data-id="element-1658">
              <button onClick={() => {
            onPaymentCountBasisChange('payment-count');
            setShowPaymentCountDropdown(false);
          }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${paymentCountBasis === 'payment-count' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-1659">
                <div className="flex-1 min-w-0" data-id="element-1660">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1661">
                    {t('components.DisputesFilters.payment_count')}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-1662">
                    {t('components.DisputesFilters.calculate_rates_based_on_the_number')}
                  </div>
                </div>
                {paymentCountBasis === 'payment-count' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1663" />}
              </button>

              <button onClick={() => {
            onPaymentCountBasisChange('payment-volume');
            setShowPaymentCountDropdown(false);
          }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${paymentCountBasis === 'payment-volume' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-1664">
                <div className="flex-1 min-w-0" data-id="element-1665">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1666">
                    {t('components.CardPaymentsBreakdown.payment_volume')}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-1667">
                    {t('components.DisputesFilters.calculate_rates_based_on_revenue_also')}
                  </div>
                </div>
                {paymentCountBasis === 'payment-volume' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1668" />}
              </button>
            </div>}
        </div>

        {/* Include EFW dropdown */}
        <div className="relative" ref={efwRef} data-id="element-1669">
          <button onClick={() => setShowEFWDropdown(!showEFWDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-1670">
            <span data-id="element-1671">
              {t('components.DisputesFilters.include_early_fraud_warnings_efw')}
            </span>
            <ChevronDownIcon size={16} data-id="element-1672" />
          </button>

          {showEFWDropdown && <div className="absolute left-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50" data-id="element-1673">
              <button onClick={() => {
            onIncludeEFWChange('exclude');
            setShowEFWDropdown(false);
          }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${includeEFW === 'exclude' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-1674">
                <div className="flex-1 min-w-0" data-id="element-1675">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1676">
                    {t('components.DisputesFilters.dont_include_early_fraud_warnings_efw')}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-1677">
                    {t('components.DisputesFilters.exclude_early_fraud_warnings_from_dispute')}
                  </div>
                </div>
                {includeEFW === 'exclude' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1678" />}
              </button>

              <button onClick={() => {
            onIncludeEFWChange('include');
            setShowEFWDropdown(false);
          }} className={`w-full text-left px-3 py-2 flex items-start gap-3 ${includeEFW === 'include' ? 'bg-blue-50' : 'hover:bg-gray-50'}`} data-id="element-1679">
                <div className="flex-1 min-w-0" data-id="element-1680">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1681">
                    {t('components.DisputesFilters.include_early_fraud_warnings_efw')}
                  </div>
                  <div className="text-xs text-gray-600 mt-0.5 leading-relaxed" data-id="element-1682">
                    {t('components.DisputesFilters.include_early_fraud_warnings_which_are')}
                  </div>
                </div>
                {includeEFW === 'include' && <CheckIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1683" />}
              </button>
            </div>}
        </div>
      </div>

      {/* Secondary Filters */}
      <div className="w-full flex flex-wrap items-center gap-2" data-id="element-1684">
        {/* Payment method category */}
        <div className="relative" ref={pmCategoryRef} data-id="element-1685">
          <button onClick={() => setShowPMCategoryDropdown(!showPMCategoryDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1686">
            <PlusIcon size={14} data-id="element-1687" />
            <span data-id="element-1688">
              {t('components.AcceptanceFilters.payment_method_category')}
            </span>
            {paymentMethodCategories.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1689">
                {paymentMethodCategories.length}
              </span>}
          </button>
          {showPMCategoryDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_category')} options={['Cards', 'Wallets', 'Bank transfers', 'Buy now pay later', 'Cash-based', 'Direct debit']} selectedOptions={paymentMethodCategories} onApply={selected => {
          setPaymentMethodCategories(selected);
          setShowPMCategoryDropdown(false);
        }} onBack={null} data-id="element-1690" />}
        </div>

        {/* Payment method */}
        <div className="relative" ref={pmMethodRef} data-id="element-1691">
          <button onClick={() => setShowPMMethodDropdown(!showPMMethodDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1692">
            <PlusIcon size={14} data-id="element-1693" />
            <span data-id="element-1694">{t('components.AcceptanceFilters.payment_method')}</span>
            {paymentMethods.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1695">
                {paymentMethods.length}
              </span>}
          </button>
          {showPMMethodDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_payment_method')} options={['Card', 'Apple Pay', 'Google Pay', 'Alipay', 'WeChat Pay', 'GrabPay', 'LINE Pay', 'TrueMoney', 'PromptPay', 'PayPal']} selectedOptions={paymentMethods} onApply={selected => {
          setPaymentMethods(selected);
          setShowPMMethodDropdown(false);
        }} onBack={null} data-id="element-1696" />}
        </div>

        {/* Funding source type */}
        <div className="relative" ref={fundingSourceRef} data-id="element-1697">
          <button onClick={() => setShowFundingSourceDropdown(!showFundingSourceDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1698">
            <PlusIcon size={14} data-id="element-1699" />
            <span data-id="element-1700">{t('components.AcceptanceFilters.funding_source_type')}</span>
            {fundingSourceTypes.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-1701">
                {fundingSourceTypes.length}
              </span>}
          </button>
          {showFundingSourceDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_funding_source')} options={['Card', 'Bank account', 'Stored balance', 'BNPL credit']} selectedOptions={fundingSourceTypes} onApply={selected => {
          setFundingSourceTypes(selected);
          setShowFundingSourceDropdown(false);
        }} onBack={null} data-id="element-1702" />}
        </div>

        {/* Card type dropdown */}
        <div className="relative" ref={cardTypeRef} data-id="element-1703">
          <button onClick={() => setShowCardTypeDropdown(!showCardTypeDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1704">
            <PlusIcon size={14} data-id="element-1705" />
            <span data-id="element-1706">{t('components.AcceptanceFilters.card_type')}</span>
          </button>

          {showCardTypeDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_card_type')} options={['Credit card', 'Debit card', 'Prepaid card']} selectedOptions={cardTypes} onApply={selected => {
          onCardTypesChange(selected);
          setShowCardTypeDropdown(false);
        }} onBack={null} data-id="element-1707" />}
        </div>

        {/* Card country dropdown */}
        <div className="relative" ref={cardCountryRef} data-id="element-1708">
          <button onClick={() => setShowCardCountryDropdown(!showCardCountryDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1709">
            <PlusIcon size={14} data-id="element-1710" />
            <span data-id="element-1711">{t('components.AcceptanceFilters.card_country')}</span>
          </button>

          {showCardCountryDropdown && <SearchableDropdown title={t('components.AcceptanceFilters.attrs.filter_by_card_country')} options={cardCountryOptions} selectedOptions={cardCountries} onApply={selected => {
          onCardCountriesChange(selected);
          setShowCardCountryDropdown(false);
        }} data-id="element-1712" />}
        </div>

        {/* Card brand dropdown */}
        <div className="relative" ref={cardBrandRef} data-id="element-1713">
          <button onClick={() => setShowCardBrandDropdown(!showCardBrandDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1714">
            <PlusIcon size={14} data-id="element-1715" />
            <span data-id="element-1716">{t('components.AcceptanceFilters.card_brand')}</span>
          </button>

          {showCardBrandDropdown && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_card_brand')} options={['Visa', 'Mastercard', 'American Express', 'Cartes Bancaires', 'Discover', 'eftpos Australia', 'Girocard', 'JCB', 'UnionPay']} selectedOptions={cardBrands} onApply={selected => {
          onCardBrandsChange(selected);
          setShowCardBrandDropdown(false);
        }} onBack={null} data-id="element-1717" />}
        </div>

        {/* More filters dropdown */}
        <div className="relative" ref={moreFiltersRef} data-id="element-1718">
          <button onClick={() => {
          setShowMoreFiltersDropdown(!showMoreFiltersDropdown);
          setMoreFiltersView('menu');
        }} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1719">
            <PlusIcon size={14} data-id="element-1720" />
            <span data-id="element-1721">{t('components.AcceptanceFilters.more_filters')}</span>
          </button>

          {showMoreFiltersDropdown && <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50" data-id="element-1722">
              {moreFiltersView === 'menu' && <>
                  {availableAdditionalFilters.map(filter => <button key={filter.id} onClick={() => handleMoreFilterClick(filter.id)} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2" data-id="element-1723">
                      <PlusIcon size={14} className="text-blue-600" data-id="element-1724" />
                      <span data-id="element-1725">{filter.label}</span>
                    </button>)}
                </>}

              {moreFiltersView === 'input-method' && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_input_method')} options={['In-person (card present)', 'Online (card-not-present)', 'Apple Pay', 'Google Pay']} selectedOptions={inputMethods} onApply={selected => handleApplyFilter(() => setInputMethods(selected))} onBack={handleBackToMenu} isInline data-id="element-1726" />}

              {moreFiltersView === 'currency' && <SearchableDropdown title={t('components.AcceptanceFilters.attrs.filter_by_currency')} options={currencyOptions} selectedOptions={currencies} onApply={selected => handleApplyFilter(() => setCurrencies(selected))} onBack={handleBackToMenu} isInline data-id="element-1727" />}

              {moreFiltersView === 'payment-country' && <SearchableDropdown title={t('components.DisputesFilters.attrs.filter_by_payment_country')} options={paymentCountryOptions} selectedOptions={paymentCountries} onApply={selected => handleApplyFilter(() => setPaymentCountries(selected))} onBack={handleBackToMenu} isInline data-id="element-1728" />}

              {moreFiltersView === '3ds-outcome' && <CompactDropdown title={t('components.AcceptanceFilters.attrs.filter_by_3ds_outcome')} options={['Successful 3DS frictionless flow', 'Successful 3DS challenge flow', 'Failed 3DS challenge', '3DS unavailable', '3DS not actioned', 'No 3DS']} selectedOptions={threeDSOutcomes} onApply={selected => handleApplyFilter(() => setThreeDSOutcomes(selected))} onBack={handleBackToMenu} isInline data-id="element-1729" />}
            </div>}
        </div>
      </div>
    </>;
}
// Compact Dropdown Component (for checkbox lists)
interface CompactDropdownProps {
  title: string;
  options: string[];
  selectedOptions: string[];
  onApply: (selected: string[]) => void;
  onBack: (() => void) | null;
  isInline?: boolean;
}
function CompactDropdown({
  title,
  options,
  selectedOptions,
  onApply,
  onBack,
  isInline = false
}: CompactDropdownProps) {
  const {
    t
  } = useTranslation('analytics');
  const [tempSelected, setTempSelected] = useState<string[]>(selectedOptions);
  const toggleOption = (option: string) => {
    if (tempSelected.includes(option)) {
      setTempSelected(tempSelected.filter(o => o !== option));
    } else {
      setTempSelected([...tempSelected, option]);
    }
  };
  const content = <>
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3" data-id="element-1730">
        {onBack && <button onClick={onBack} className="p-1 hover:bg-gray-100 rounded" data-id="element-1731">
            <ChevronLeftIcon size={18} className="text-gray-600" data-id="element-1732" />
          </button>}
        <h3 className="text-base font-semibold text-gray-900" data-id="element-1733">{title}</h3>
      </div>
      <div className="flex-1 overflow-y-auto py-1" data-id="element-1734">
        {options.map(option => <label key={option} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer" data-id="element-1735">
            <input type="checkbox" checked={tempSelected.includes(option)} onChange={() => toggleOption(option)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1736" />

            <span className="text-base text-gray-900" data-id="element-1737">{option}</span>
          </label>)}
      </div>
      <div className="px-4 py-3 border-t border-gray-200" data-id="element-1738">
        <button onClick={() => onApply(tempSelected)} className="w-full px-4 py-2.5 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-id="element-1739">
          {t('components.content.apply')}
        </button>
      </div>
    </>;
  if (isInline) {
    return <div className="flex flex-col max-h-96" data-id="element-1740">{content}</div>;
  }
  return <div className="absolute left-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 flex flex-col" data-id="element-1741">
      {content}
    </div>;
}
// Searchable Dropdown Component (for card country and currency)
interface SearchableDropdownProps {
  title: string;
  options: string[];
  selectedOptions: string[];
  onApply: (selected: string[]) => void;
  onBack?: (() => void) | null;
  isInline?: boolean;
}
function SearchableDropdown({
  title,
  options,
  selectedOptions,
  onApply,
  onBack,
  isInline = false
}: SearchableDropdownProps) {
  const {
    t
  } = useTranslation('analytics');
  const [tempSelected, setTempSelected] = useState<string[]>(selectedOptions);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredOptions = options.filter(option => option.toLowerCase().includes(searchQuery.toLowerCase()));
  const toggleOption = (option: string) => {
    if (tempSelected.includes(option)) {
      setTempSelected(tempSelected.filter(o => o !== option));
    } else {
      setTempSelected([...tempSelected, option]);
    }
  };
  const content = <>
      <div className="px-4 py-3 border-b border-gray-200" data-id="element-1742">
        <div className="flex items-center gap-3 mb-2" data-id="element-1743">
          {onBack && <button onClick={onBack} className="p-1 hover:bg-gray-100 rounded" data-id="element-1744">
              <ChevronLeftIcon size={18} className="text-gray-600" data-id="element-1745" />
            </button>}
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-1746">{title}</h3>
        </div>
        <div className="relative" data-id="element-1747">
          <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1748" />

          <input type="search" aria-label={t('components.content.attrs.search')} placeholder={t('components.content.attrs.search')} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1749" />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-1" data-id="element-1750">
        {filteredOptions.map(option => <label key={option} className="flex items-center gap-3 px-4 py-2 hover:bg-gray-50 cursor-pointer" data-id="element-1751">
            <input type="checkbox" checked={tempSelected.includes(option)} onChange={() => toggleOption(option)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1752" />

            <span className="text-sm text-gray-900" data-id="element-1753">{option}</span>
          </label>)}
      </div>
      <div className="px-4 py-3 border-t border-gray-200" data-id="element-1754">
        <button onClick={() => onApply(tempSelected)} className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-id="element-1755">
          {t('components.content.apply')}
        </button>
      </div>
    </>;
  if (isInline) {
    return <div className="flex flex-col max-h-96" data-id="element-1756">{content}</div>;
  }
  return <div className="absolute left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 flex flex-col" data-id="element-1757">
      {content}
    </div>;
}